import { FloatingBackButton } from "@components/atoms/FloatingBackButton"
import { ArticleCard } from "@components/cards/ArticleCard"
import { AwardEntryMicroCard } from "@components/cards/AwardEntryMicroCard"
import { Accordion } from "@components/controls/Accordion"
import { ChipButton, SocialButton } from "@components/controls/Button"
import { DirectoryHeader } from "@components/layout/DirectoryHeader"
import { ResultList } from "@components/layout/ResultList"
import { ProfileGalleryImage } from "@components/media/ProfileGalleryImage"
import { StaggerTransitionRoot } from "@components/motion/Transition"
import { useMembershipStatus } from "@features/accounts/hooks/useAuth"
import { useProfileEditor } from "@features/accounts/hooks/useProfileEditor"
import { UserOrBusinessProfile } from "@features/accounts/profile-type"
import { useMyBusinessProfile, useMyProfile } from "@hooks/queries"
import { Pencil1Icon } from "@radix-ui/react-icons"
import { MakeURL } from "@utils/create-url"
import { defineView } from "eddev/views"
import { ReactNode, useEffect, useMemo, useState } from "react"
import { ArticleCardFragment, AwardMicroCardFragment, ProfileGalleryItemFragment } from "../types.graphql"

const SOCIAL_NAME = {
  website: "Website",
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  x: "𝕏",
} as Record<string, string>

export default defineView("single-profile", (props) => {
  const myself = useMembershipStatus()
  const isBusiness = props.business && !props.designerProfile
  const [user, setUser] = useState<UserOrBusinessProfile>(props.business ?? props.designerProfile!)
  let isMe = isBusiness ? myself.ownsBusinessId && myself.ownsBusinessId === props.business?.databaseId! : myself.id === user?.id

  useEffect(() => {
    if (isMe) {
      if (isBusiness) {
        useMyBusinessProfile
          .fetch(undefined, {
            staleTime: 1,
          })
          .then((data) => {
            setUser(data?.customer?.business!)
          })
      } else {
        useMyProfile
          .fetch(undefined, {
            staleTime: 1,
          })
          .then((data) => {
            setUser(data?.customer?.user!)
          })
      }
    } else if (props.business) {
      setUser(props.business)
    } else {
      setUser(props.designerProfile!)
    }
  }, [isMe])

  const profileEditor = useProfileEditor({
    data: user,
    onChange: setUser,
  })

  const [tab, setTab] = useState("")

  const [previewImage, setPreviewImage] = useState<ProfileGalleryItemFragment | null>(user?.selectedGalleryItems?.[0]! ?? null)

  const awards = useMemo(() => {
    const byYear = new Map<number, AwardMicroCardFragment[]>()

    let rows: ReactNode[] = []

    user?.awards?.forEach((item) => {
      const year = item?.category?.season?.year
      if (!year) return
      byYear.set(year, [...(byYear.get(year) || []), item])
    })
    ;[...byYear.entries()]
      .sort(([y1], [y2]) => (y1 < y2 ? 1 : -1))
      .forEach(([year, items]) => {
        rows.push(
          <div key={year} className="mb-6">
            <p className="type-title-m-light border-b border-black pb-3">{year}</p>
            {items.map((item, i) => (
              <AwardEntryMicroCard key={item.id} {...item} />
            ))}
          </div>
        )
      })

    if (rows.length === 0) return null
    return <div>{rows}</div>
  }, [isMe, user])

  const experience = useMemo(() => {
    const items = user?.publicProfileData?.experience?.filter((e) => !!e?.name || !!e?.role)
    if (!items?.length && !isMe) return null
    return (
      <div className="flex flex-col gap-4">
        {!items?.length && isMe && <div className="type-body-m-roman text-grey-dark">You have no experience to show</div>}
        {items?.map((item, i) => {
          return (
            <div key={`exp${i}`} className="flex flex-col type-body-m-roman">
              {!!item?.name && <p className="font-semibold mb-1">{item?.name}</p>}
              {!!item?.role ? (
                <p>
                  {item.role}{" "}
                  {!!item.from && (
                    <span className="text-grey type-ui-m-light">
                      {item.from}
                      {!!item.to && ` — ${item.to}`}
                    </span>
                  )}
                </p>
              ) : item?.from ? (
                <p className="text-grey">
                  {item.from} — {item.to}
                </p>
              ) : null}
            </div>
          )
        })}
        {isMe && (
          <div className="mt-1">
            <EditButton
              label="Edit Experience"
              enabled
              onClick={() => {
                profileEditor.open("experience")
              }}
            />
          </div>
        )}
      </div>
    )
  }, [isMe, user])

  const expertise = useMemo(() => {
    const items = user?.publicProfileData?.expertise?.filter((e) => !!e?.name)
    if (!items?.length && !isMe) return null
    return (
      <div className="flex flex-col gap-1">
        {!items?.length && isMe && <div className="type-body-m-roman text-grey-dark">You have no expertise to show</div>}
        {items
          ?.sort((a, b) => a?.name!.localeCompare(b?.name!)!)
          .map((e, i) => (
            <div className="type-body-m-roman" key={i}>
              {e?.name}
            </div>
          ))}
        {isMe && (
          <div className="mt-4">
            <EditButton
              label="Edit Expertise"
              enabled
              onClick={() => {
                profileEditor.open("expertise")
              }}
            />
          </div>
        )}
      </div>
    )
  }, [isMe, user])

  const clients = useMemo(() => {
    const intro = user?.publicProfileData?.featuredClients?.introduction
    const items = user?.publicProfileData?.featuredClients?.clients?.filter((c) => !!c?.name)

    if ((!intro || !items?.length) && !isMe) return null
    return (
      <div className="mb-4">
        <div className="stack-y-5">
          {!!intro && <p className="type-body-m-roman whitespace-pre-line">{intro.trim()}</p>}
          {isMe && !items?.length && !intro && <div className="type-body-m-roman text-grey-dark">You have no clients to show</div>}
          {items?.length! > 0 &&
            items?.map((c, i) => (
              <div key={i} className="flex flex-col">
                {!!c?.name && <p className="type-title-xs-medium">{c?.name}</p>}
                {!!c?.description && <p className="type-title-xs-roman">{c.description}</p>}
              </div>
            ))}
        </div>
        {isMe && (
          <div className="mt-5">
            <EditButton
              label="Edit Selected Clients"
              enabled
              onClick={() => {
                profileEditor.open("clients")
              }}
            />
          </div>
        )}
      </div>
    )
  }, [isMe, user])

  // if (isMe && myProfile && myProfile.isLoading) {
  //   return <LoadingResults />
  // }

  if (!user) return null

  const accordionTabs = [
    {
      value: "awards",
      label: "Awards",
      body: awards,
    },
    {
      value: "experience",
      label: "Experience",
      body: experience,
    },
    {
      value: "expertise",
      label: "Expertise",
      body: expertise,
    },
    {
      value: "clients",
      label: "Selected clients",
      body: clients,
    },
    // {
    //   value: "involvement",
    //   label: "Involvement",
    //   body: <p>Some thing here</p>,
    // },
  ]

  return (
    <>
      <StaggerTransitionRoot>
        <div className="grid-auto h-full mx-auto items-center mb-10 mt-6">
          <div className="col-span-12">
            <h2 data-transition="fade" className="type-title-xl-medium font-500 pb-3 border-b border-black leading-none">
              <div> {`${user.__typename === "Business" ? user.title : user?.firstName + " " + user?.lastName}`}</div>
              <EditButton
                label="Edit Name"
                enabled={isMe}
                onClick={() => {
                  profileEditor.open("businessName")
                }}
              />
            </h2>
          </div>
          <div className="col-span-12 grid-auto py-4">
            {/* Title and Branch */}
            <div className="col-span-12 md:col-span-4">
              <div className="flex flex-col gap-1">
                {(user?.publicProfileData?.title || isMe) && (
                  <div className="flex gap-5 justify-between items-center">
                    {user?.publicProfileData?.title && (
                      <p data-transition="fade" className="type-title-s-light">
                        {user?.publicProfileData?.title}
                      </p>
                    )}
                    {isMe && !isBusiness && (
                      <div>
                        <EditButton
                          label="Edit Title"
                          enabled={isMe}
                          onClick={() => {
                            profileEditor.open("title")
                          }}
                        />
                      </div>
                    )}
                    {isMe && isBusiness && (
                      <div>
                        <EditButton
                          label="Edit"
                          enabled={isMe}
                          onClick={() => {
                            profileEditor.open("businessTypeAndLocation")
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
                <div data-transition="children/fade" className="flex gap-2 justify-between items-center">
                  <p className="type-body-s-roman">{user.__typename === "Business" ? "Design Business" : user.isStudent ? "Student" : "Professional"}</p>
                  {user.__typename === "Business" ? (
                    <div className="flex gap-2 flex-wrap">
                      {user?.businessData?.branch?.map((branch, i) => (
                        <ChipButton key={i} href={MakeURL.findDesigner({ location: branch?.slug! })} variant="outline">
                          {branch?.shortName}
                        </ChipButton>
                      ))}
                    </div>
                  ) : (
                    <ChipButton href={MakeURL.findDesigner({ location: user?.memberData?.branch?.slug! })} variant="outline">
                      {user?.memberData?.branch?.shortName}
                    </ChipButton>
                  )}
                </div>
              </div>
            </div>

            {/* Mini Gallery */}
            <div className="hidden md:block col-span-8">
              {user?.selectedGalleryItems?.length! > 0 && (
                <div className="flex flex-wrap h-10 overflow-hidden gap-4" data-transition="children/fade">
                  {user?.selectedGalleryItems?.slice(0, 10)?.map((item, i) => (
                    <button key={i} className="h-full" onPointerEnter={() => setPreviewImage(item)}>
                      <ProfileGalleryImage item={item!} className="h-full" />
                    </button>
                  ))}
                </div>
              )}
              {isMe && !user?.selectedGalleryItems?.length && (
                <div
                  className="flex flex-wrap h-10 overflow-hidden gap-4 border border-dashed border-grey/50 rounded-[4px] aspect-[5/3] type-ui-s-roman-caps items-center justify-center"
                  data-transition="children/fade"
                >
                  <div className="stack-x-0 items-center">No Images</div>
                </div>
              )}
              {isMe && (
                <div className="mt-3">
                  <EditButton
                    label="Choose Images"
                    enabled
                    className="mt-4"
                    onClick={() => {
                      profileEditor.open("gallery")
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="col-span-12 grid-auto">
            <div className="col-span-12 md:col-span-4 flex flex-col gap-5">
              {/* Avatar */}
              {!!previewImage && (
                <div data-transition="fade" className="w-full aspect-[4/3] relative object-top hidden md:block">
                  <ProfileGalleryImage item={previewImage!} max objectFit="contain" objectPosition="center" />
                </div>
              )}
              {/* Sliding Gallery (Mobile) */}
              <div className="-mx-[16px] flex overflow-auto hide-scrollbars snap-x snap-mandatory md:hidden">
                {user?.selectedGalleryItems?.slice(0, 10)?.map((item, i) => (
                  <div key={i} className="flex-none h-[200px] pl-[16px] last:pr-[16px] snap-start snap-always">
                    <ProfileGalleryImage item={item!} className="h-full" />
                  </div>
                ))}
              </div>
              {/* Social Links */}
              {user?.publicProfileData?.links?.length! > 0 && (
                <div className="flex gap-2" data-transition="children/fade">
                  {user?.publicProfileData?.links?.map((l, i) => (
                    <SocialButton key={l?.type} href={l?.url}>
                      {SOCIAL_NAME[l?.type!] ?? l?.type}
                    </SocialButton>
                  ))}
                </div>
              )}
              {/* Contact Details */}
              <div className="flex flex-col gap-2 type-body-s-roman" data-transition="children/fade">
                {user?.publicProfileData?.publicEmail && (
                  <div className="flex flex-col gap-0 type-body-s-roman">
                    <p>Email</p>
                    <p>
                      <a href={`mailto:${user.publicProfileData.publicEmail}`}>{user.publicProfileData.publicEmail}</a>
                    </p>
                  </div>
                )}
                {user?.publicProfileData?.publicPhone && (
                  <div className="flex flex-col gap-0 type-body-s-roman">
                    <p>Phone</p>
                    <p>
                      <a href={`tel:${user.publicProfileData.publicPhone}`}>{user.publicProfileData.publicPhone}</a>
                    </p>
                  </div>
                )}
                <EditButton
                  label="Edit Contact Details"
                  enabled={!!isMe}
                  onClick={() => {
                    profileEditor.open("contact")
                  }}
                />
                <div className="flex flex-col gap-0 text-grey-mid">
                  {user?.registeredDate && (
                    <p>
                      Member since{" "}
                      {new Date(user?.registeredDate).toLocaleDateString("en-au", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  )}
                  {user?.lastUpdated && (
                    <p>
                      Profile information last updated{" "}
                      {new Date(user?.lastUpdated).toLocaleDateString("en-au", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-5 col-span-12 md:col-span-8">
              <div className="border-b border-black mb-3 mt-9 md:hidden"></div>
              <div data-transition="children/fade">
                {!!user?.publicProfileData?.introduction && (
                  <div className="w-full whitespace-pre-line type-body-m-serif">
                    <p>{user.publicProfileData.introduction?.trim()}</p>
                  </div>
                )}
                {!user?.publicProfileData?.introduction && isMe && <div className="type-body-m-roman">You have no introduction to show</div>}

                {isMe && (
                  <div className="mt-3">
                    <EditButton
                      label="Edit Introduction"
                      enabled={isMe}
                      onClick={() => {
                        profileEditor.open("introduction")
                      }}
                    />
                  </div>
                )}
              </div>

              <Accordion className="mt-2 md:mt-0 mb-2" type="single" value={tab} onValueChange={setTab} options={accordionTabs.filter((item) => !!item.body)} />

              {/* <div>
            <Button className="w-full md:w-auto" variant="charcoal">
              Back to Directory
            </Button>
          </div> */}
            </div>
          </div>
          {user.__typename === "User" && (
            <AuthoredContent
              name={user.firstName!}
              content={
                user.authoredContent?.filter((n) => {
                  return n?.__typename === "Article"
                }) ?? []
              }
            />
          )}
        </div>
        <div className="h-5" />
      </StaggerTransitionRoot>
      <FloatingBackButton />
    </>
  )
})

export function AuthoredContent(props: { name: string; content: ArticleCardFragment[] }) {
  if (props.content.length === 0) return null

  return (
    <div className="col-span-12 mt-10">
      <DirectoryHeader heading={props.name ? `${props.name}'s authored content` : "Articles by this user"} />
      <div className="grid-auto">
        <ResultList component={ArticleCard} mobileMode="row" dynamicSizing="totalDependant" items={props.content} mode="card" keyField="id" />
      </div>
    </div>
  )
}

function EditButton(props: { label: string; onClick: () => void; enabled: boolean; className?: string }) {
  if (!props.enabled) return null
  return (
    <div data-transition="fade" className={props.className}>
      <ChipButton
        onClick={props.onClick}
        className="flex gap-1 items-center type-body-s-roman text-grey-dark hover:text-grey-mid cursor-pointer"
        disabled={!props.enabled}
        leftIcon={<Pencil1Icon />}
        variant="outline"
      >
        <span>{props.label}</span>
      </ChipButton>
    </div>
  )
}
